import {
  Box,
  Button,
  CircularProgress,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ResponsiveDialog from 'app/shared-components/layout/dialog/ResponsiveDialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IntacctEntity } from 'app/services/erp-connection.service';

/**
 * SEE-1261: sentinel Select value for the explicit multi-entity "top level"
 * choice — a company-wide activation that syncs EVERY entity's codes into this
 * paidol (Agave parity). Non-empty so it satisfies the Confirm-enabled check.
 */
export const TOP_LEVEL_VALUE = '__TOP_LEVEL__';

interface IntacctEntityPickerModalProps {
  open: boolean;
  entities: IntacctEntity[];
  /** True while the select mutation is in flight. */
  submitting: boolean;
  /** True during the initial entity-list fetch (modal opens before it resolves). */
  loading?: boolean;
  /** True while re-fetching the entity list. */
  refreshing?: boolean;
  /**
   * Confirm the choice: a specific entityId, or topLevel=true for the
   * company-wide (all-entities) activation (SEE-1261).
   */
  onConfirm: (entityId: string | null, topLevel: boolean) => void;
  /** Re-fetch the entity list (matches Agave's refresh affordance). */
  onRefresh?: () => void;
  /** Cancel the connection (disconnects this paidol). Opens a confirm first. */
  onCancel?: () => void;
  /** True while the cancel/disconnect mutation is in flight. */
  cancelling?: boolean;
}

/**
 * SEE-1062 — post-OAuth entity picker for a top-level Sage Intacct user.
 *
 * A multi-entity Sage company exposes every entity's coding data; picking one
 * here scopes the connection (sync + posting + dropdowns) to that entity,
 * matching Agave's pick-at-connect behavior. The modal stays open until the
 * user either picks an entity or cancels — cancelling disconnects this paidol
 * (SEE-863 semantics: unlink only; tear the shared connection down only if this
 * was the last paidol), since a PENDING entity selection is not a usable
 * connected state.
 *
 * Layout mirrors Agave's entity picker: an "Entity" dropdown with a refresh
 * affordance beside it, a full-width Confirm beneath, and a Cancel below that.
 */
function IntacctEntityPickerModal({
  open,
  entities,
  submitting,
  loading = false,
  refreshing = false,
  onConfirm,
  onRefresh,
  onCancel,
  cancelling = false,
}: IntacctEntityPickerModalProps) {
  const { t } = useTranslation();
  const [selectedEntityId, setSelectedEntityId] = useState<string>('');

  const labelFor = (entity: IntacctEntity) => (entity.name ? `${entity.name} (${entity.id})` : entity.id);

  // The modal opens immediately on redirect, before the live Sage entity list
  // resolves — show a spinner placeholder in the dropdown until it arrives.
  const showLoadingPlaceholder = loading && entities.length === 0;

  return (
    <ResponsiveDialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>{t('erpConnection.selectEntityTitle', 'Select a Sage Intacct entity')}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2.5 }}>
          {t(
            'erpConnection.selectEntityDescription',
            'Your Sage Intacct login can access multiple entities. Choose a single entity to sync and code only its projects, cost codes, and locations — or choose "All entities (Top level)" to sync every entity\'s codes into this company. Either way, each transaction still posts through the posting account you select.'
          )}
        </DialogContentText>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {showLoadingPlaceholder ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                minHeight: 56,
                px: 1.75,
                border: 1,
                borderColor: 'rgba(0, 0, 0, 0.23)',
                borderRadius: 1,
                color: 'text.secondary',
              }}
            >
              <CircularProgress size={18} />
              {t('erpConnection.loadingEntities', 'Loading entities…')}
            </Box>
          ) : (
            <FormControl fullWidth>
              <InputLabel id="intacct-entity-select-label">
                {t('erpConnection.entityLabel', 'Entity')}
              </InputLabel>
              <Select
                labelId="intacct-entity-select-label"
                label={t('erpConnection.entityLabel', 'Entity')}
                value={selectedEntityId}
                onChange={(e: SelectChangeEvent) => setSelectedEntityId(e.target.value)}
                disabled={submitting || refreshing}
              >
                <MenuItem value={TOP_LEVEL_VALUE}>
                  {t('erpConnection.entityTopLevel', 'All entities (Top level)')}
                </MenuItem>
                <Divider />
                {entities.map((entity) => (
                  <MenuItem key={entity.id} value={entity.id}>
                    {labelFor(entity)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {onRefresh && (
            <IconButton
              aria-label={t('erpConnection.refreshEntities', 'Refresh entities')}
              onClick={onRefresh}
              disabled={submitting || refreshing || showLoadingPlaceholder}
            >
              {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          )}
        </Box>
        <Button
          variant="contained"
          fullWidth
          sx={{ mt: 2.5 }}
          disabled={!selectedEntityId || submitting || refreshing || cancelling || showLoadingPlaceholder}
          onClick={() =>
            selectedEntityId === TOP_LEVEL_VALUE ? onConfirm(null, true) : onConfirm(selectedEntityId, false)
          }
        >
          {submitting ? t('loading') : t('erpConnection.confirmEntity', 'Confirm')}
        </Button>
        {onCancel && (
          <Button
            variant="text"
            fullWidth
            sx={{ mt: 1 }}
            disabled={submitting || cancelling}
            onClick={onCancel}
          >
            {cancelling ? t('loading') : t('cancel')}
          </Button>
        )}
      </DialogContent>
    </ResponsiveDialog>
  );
}

export default IntacctEntityPickerModal;
